import type { Card, CardChart } from '@/features/dashboard/api/schemas'
import {
  formatLike,
  toFigure,
  valueOptions,
  type FigureOptions,
} from '@/features/dashboard/format/figure'
import { formatChangeIn } from '@/features/dashboard/format/format'
import { isNetCategory } from './categoryOptions'
import { deepDiveModel } from './deepDiveModel'
import { formatChartValue } from './tooltipHtml'

/** A card's latest value and change, e.g. "€12.5M, +4% on the previous period". */
export const valueSummary = (
  card: Card,
  options?: FigureOptions
): string | null => {
  const figure = toFigure(card, options)
  if (!figure) return null
  const change =
    figure.change === null
      ? ''
      : `, ${formatChangeIn(figure.change, figure.changeUnit, valueOptions(figure))} on the previous period`
  return `${formatLike(figure, figure.value)}${change}`
}

const HBAR_LISTED = 5

/** A value as the card reads it; a delta also carries its sign. */
const amount = (card: Card, value: number, signed = false) => {
  const text =
    card.kind === 'chart' || card.kind === 'kpi'
      ? formatChartValue(value, card.unit, card.format, 1)
      : String(value)
  return signed && value > 0 ? `+${text}` : text
}

const share = (part: number, total: number) =>
  `${total > 0 ? Math.round((part / total) * 100) : 0}%`

/**
 * What a category chart says in words: the donut's shares, the delta columns'
 * direction, the horizontal bars' ranking, a 100% column's latest shares.
 */
const categorySummary = (
  card: Card,
  chart: CardChart | null | undefined
): string | null => {
  if (card.kind !== 'chart' || card.categories.length === 0) return null
  const model = deepDiveModel(card, chart)
  const { labels, totals } = model
  const pairs = labels.map((label, i) => ({ label, total: totals[i] ?? 0 }))
  if (model.kind === 'pie') {
    const sum = pairs.reduce((a, { total }) => a + Math.max(total, 0), 0)
    return `${pairs
      .map(
        ({ label, total }) =>
          `${label} ${amount(card, total)} (${share(Math.max(total, 0), sum)})`
      )
      .join(', ')}; total ${amount(card, sum)}`
  }
  if (model.kind === 'waterfall') {
    const net = pairs
      .filter(({ label }) => !isNetCategory(label))
      .reduce((a, { total }) => a + total, 0)
    const direction = net > 0 ? 'up' : net < 0 ? 'down' : 'flat'
    return `net ${direction} ${amount(card, net, true)}; ${pairs
      .map(({ label, total }) => `${label} ${amount(card, total, true)}`)
      .join(', ')}`
  }
  if (model.kind === 'hbar') {
    const listed = pairs.slice(0, HBAR_LISTED)
    const more = pairs.length - listed.length
    return `largest first: ${listed
      .map(({ label, total }) => `${label} ${amount(card, total)}`)
      .join(', ')}${more > 0 ? `, and ${more} more` : ''}`
  }
  if (model.percent) {
    const last = labels.length - 1
    const total = totals[last] ?? 0
    return `share of ${labels[last]}: ${model.metrics
      .map(row => `${row.name} ${share(row.data[last] ?? 0, total)}`)
      .join(', ')}`
  }
  return null
}

/**
 * A chart's text alternative: the card's name, latest value and change; for a
 * donut, delta columns, horizontal bars or 100% columns what the chart shows.
 */
export const chartSummary = (
  card: Card,
  options?: FigureOptions,
  chart?: CardChart | null
): string => {
  const categories = categorySummary(card, chart)
  if (categories !== null) return `${card.name}: ${categories}`
  const summary = valueSummary(card, options)
  return summary === null ? card.name : `${card.name}: ${summary}`
}
